import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import arrowDownIcon from "../../assets/icons/arrow_right.svg";
import subfolderIcon from "../../assets/icons/subfolder.svg";
import { FolderTreeType } from "./segment-interface";

export function FolderTree({ data, defaultSelectedPath, setSelectedTag }: FolderTreeType) {
  const methods = useFormContext();
  const { getValues, setValue } = methods;

  const selectedCountry = getValues("country");

  const [openMap, setOpenMap] = useState<{ [path: string]: boolean }>({});
  const [segmentSelected, setSegmentSelected] = useState<string>("");

  const buildPath = (parts: string[]) => parts.join("/");

  const resolveTagForPath = (parts: string[]) => {
    const [country, brand] = parts;
    if (!country || !brand) return undefined;

    const countryNode = data.find((item) => item?.Country === country);
    if (!countryNode || !Array.isArray(countryNode.Children)) return undefined;

    const brandNode = countryNode.Children.find((item: any) => item?.Brand === brand);
    return brandNode?.Tag;
  };

  const toggleOpen = (path: string) => {
    setOpenMap((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const handleSelect = (path: string, tag?: string) => {
    setSegmentSelected(path);
    setValue("path", path);
    if (tag && setSelectedTag) {
      setSelectedTag(tag); // send tag to parent
    }
  };

  useEffect(() => {
    if (defaultSelectedPath.length > 0) {
      const open: { [key: string]: boolean } = {};

      for (let i = 1; i <= defaultSelectedPath.length; i++) {
        const path = buildPath(defaultSelectedPath.slice(0, i));
        open[path] = true;
      }

      setOpenMap(open);
      setSegmentSelected(buildPath(defaultSelectedPath));
      setValue("path", buildPath(defaultSelectedPath));

      const defaultTag = resolveTagForPath(defaultSelectedPath);
      if (defaultTag && setSelectedTag) {
        setSelectedTag(defaultTag);
      }
    }
  }, [defaultSelectedPath.join("/")]);

  const renderChildrenLevel = (childrenArray: string[], parentPath: string[], brandTag?: string) => {
    return childrenArray.map((child) => {
      const path = buildPath([...parentPath, child]);
      const isSelected = segmentSelected === path;
      return (
        <Box
          key={path}
          sx={{
            pl: 5,
          }}
          onClick={() => handleSelect(path, brandTag)}
          className={isSelected ? "selected channel" : "channel"}
        >
          <Box component="img" src={subfolderIcon} alt="folder icon" className="folder-icon" />
          <Typography variant="body2">{child}</Typography>
        </Box>
      );
    });
  };

  const renderBrandLevel = (brands: any[], parentPath: string[]) => {
    return brands.map((brandItem) => {
      const path = buildPath([...parentPath, brandItem.Brand]);
      const isOpen = !!openMap[path];
      return (
        <Box key={path} sx={{ ml: parentPath.length > 0 ? 2 : 0, mb: "2px" }}>
          <Box
            className={isOpen ? "open brand" : "brand"}
            onClick={() => {
              handleSelect(path, brandItem.Tag); // select brand and send tag
              toggleOpen(path); // also toggle open/close
            }}
          >
            <Box
              component="img"
              src={arrowDownIcon}
              alt={isOpen ? "Collapse" : "Expand"}
              sx={{
                transform: isOpen ? "rotate(0deg)" : "rotate(270deg)",
                transition: "transform 0.2s",
              }}
              className="arrow-icon"
            />
            <Box component="img" src={subfolderIcon} alt="folder icon" className="folder-icon" />
            <Typography variant="subtitle2">{brandItem.Brand}</Typography>
          </Box>

          {isOpen &&
            brandItem.Children &&
            renderChildrenLevel(brandItem.Children, [...parentPath, brandItem.Brand], brandItem.Tag)}
        </Box>
      );
    });
  };

  const filteredData = data.filter((countryItem) => countryItem.Country === selectedCountry);

  const renderList = () => {
    return filteredData.map((countryItem) => {
      const path = buildPath([countryItem.Country]);
      const isOpen = !!openMap[path];
      return (
        <Box key={path}>
          {countryItem.Children && renderBrandLevel(countryItem.Children, [countryItem.Country])}
        </Box>
      );
    });
  };

  return <Box>{renderList()}</Box>;
}
